import type { BlogPostBlock, BlogPostRecord } from "./schema.ts";

/**
 * Calculates human-readable reading time from content blocks using
 * canonical 220 words-per-minute heuristic.
 */
export function calculateReadingTime(
  content: BlogPostBlock[],
  wordsPerMinute = 220,
): string {
  const words = content.reduce((total, block) => {
    const value =
      block.type === "list"
        ? block.items.join(" ")
        : block.type === "code"
          ? block.tabs
            ? block.tabs.map((tab) => tab.code).join(" ")
            : (block.code ?? "")
          : block.type === "table"
            ? [block.caption, ...block.columns, ...block.rows.flat()].join(" ")
            : block.type === "figure"
              ? (block.caption ?? "")
              : block.text;

    return total + value.trim().split(/\s+/).filter(Boolean).length;
  }, 0);

  return `${Math.max(1, Math.ceil(words / wordsPerMinute))} min read`;
}

export function isPublishedByDate<
  T extends Pick<BlogPostRecord, "status" | "publishedAt" | "image">,
>(
  post: T,
  now = new Date(),
): post is T & { status: "published"; image: string } {
  return (
    post.status === "published" &&
    Date.parse(`${post.publishedAt}T00:00:00Z`) <= now.getTime()
  );
}

export function isPublicBlogPost<
  T extends Pick<BlogPostRecord, "status" | "publishedAt" | "image">,
>(post: T): post is T & { status: "published"; image: string } {
  return isPublishedByDate(post);
}

/** Preview context is supplied by the frontend; publication gates stay pure. */
export function isPreviewableBlogPost(post: BlogPostRecord): boolean {
  return isPublishedByDate(post);
}

export function sortBlogPosts<
  T extends Pick<BlogPostRecord, "status" | "publishedAt">,
>(posts: readonly T[]): T[] {
  return [...posts].sort((a, b) => {
    const aHasDate = a.status === "ready" || a.status === "published";
    const bHasDate = b.status === "ready" || b.status === "published";
    if (aHasDate && bHasDate)
      return Date.parse(b.publishedAt) - Date.parse(a.publishedAt);
    if (aHasDate) return -1;
    if (bHasDate) return 1;
    if (a.status !== b.status) return a.status === "draft" ? -1 : 1;
    return Date.parse(b.publishedAt) - Date.parse(a.publishedAt);
  });
}
